import React, { useState, useEffect, useContext } from 'react';
import { Card, Descriptions, Spin, message, Avatar, Row, Col, Tag, Divider, Alert } from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  CalendarOutlined, 
  ManOutlined, 
  TagOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import { LoginContext } from '../../context/loginContext';
import dayjs from 'dayjs';

const AlumnoPerfil = () => {
  const { user } = useContext(LoginContext);
  const [alumno, setAlumno] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadAlumno();
  }, []);

  const loadAlumno = async () => {
    setLoading(true);
    try {
      // Obtener el perfil del alumno usando la ruta específica
      const response = await apiClient.get('/mi/perfil');
      
      if (response.ok) {
        const alumnoData = await response.json();
        setAlumno(alumnoData);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los datos del perfil');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los datos del perfil');
    } finally {
      setLoading(false);
    }
  };


  return (
    <MainLayout>
      <div style={{ 
        padding: isMobile ? '16px' : '24px',
        minHeight: 'calc(100vh - 64px)',
        background: '#1a1a1a',
      }}>
        <div style={{ 
          maxWidth: '900px', 
          margin: '0 auto' 
        }}>
          <h1 style={{ 
            color: '#ffffff', 
            marginBottom: isMobile ? '24px' : '32px',
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <UserOutlined style={{ fontSize: isMobile ? '22px' : '28px' }} />
            Mi Perfil
          </h1>

          <Spin spinning={loading}>
            {alumno && alumno.saldoCreditos <= 0 && (
              <Alert
                message={alumno.saldoCreditos <= -2 ? 'Atención: sin créditos' : 'Créditos bajos'}
                description={
                  alumno.saldoCreditos <= -2
                    ? 'Tenés -2 créditos. No podrás fichar en el club hasta que compres créditos.'
                    : alumno.saldoCreditos === 0
                      ? 'Te quedaste sin créditos. Comprá antes de tu próxima clase.'
                      : `Tenés ${alumno.saldoCreditos} créditos. Comprá créditos para seguir asistiendo sin inconvenientes.`
                }
                type="error"
                icon={<ExclamationCircleOutlined />}
                showIcon
                style={{
                  marginBottom: isMobile ? '20px' : '24px',
                  borderRadius: '8px',
                }}
              />
            )}
            {alumno && (
              <Card
                style={{
                  background: 'linear-gradient(135deg, #2d2d2d 0%, #3a3a3a 100%)',
                  border: '1px solid #404040',
                  borderRadius: '16px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                  overflow: 'hidden',
                }}
                bodyStyle={{
                  padding: isMobile ? '20px' : '40px',
                }}
              >
                {/* Header con foto y nombre */}
                <div style={{ 
                  textAlign: 'center',
                  marginBottom: isMobile ? '24px' : '40px',
                  paddingBottom: isMobile ? '20px' : '32px',
                  borderBottom: '1px solid #404040',
                }}>
                  <div style={{ 
                    position: 'relative',
                    display: 'inline-block',
                    marginBottom: '20px'
                  }}>
                    <Avatar
                      size={isMobile ? 100 : 140}
                      src={alumno.alu_foto || null}
                      icon={!alumno.alu_foto && <UserOutlined style={{ fontSize: isMobile ? '40px' : '60px' }} />}
                      style={{
                        border: '4px solid #666666',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
                        background: alumno.alu_foto ? 'transparent' : '#404040',
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: alumno.alu_activo ? '#52c41a' : '#ff4d4f',
                      border: '3px solid #2d2d2d',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    }} />
                  </div>
                  <h2 style={{ 
                    color: '#ffffff',
                    fontSize: isMobile ? '18px' : '24px',
                    fontWeight: 600,
                    margin: '8px 0',
                    marginBottom: '4px'
                  }}>
                    {alumno.alu_nombre} {alumno.alu_apellido}
                  </h2>
                  <Tag 
                    color={alumno.alu_activo ? 'success' : 'error'}
                    icon={alumno.alu_activo ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    style={{
                      fontSize: '13px',
                      padding: '4px 12px',
                      borderRadius: '12px',
                    }}
                  >
                    {alumno.alu_activo ? 'Activo' : 'Inactivo'}
                  </Tag>
                </div>

                {/* Información Personal */}
                <div>
                  <h3 style={{
                    color: '#ffffff',
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: 600,
                    marginBottom: isMobile ? '16px' : '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <UserOutlined />
                    Información Personal
                  </h3>

                  <Row gutter={isMobile ? [16, 16] : [24, 24]}>
                    <Col xs={24} sm={12}>
                      <div style={{
                        background: '#1a1a1a',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #404040',
                        height: '100%',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#666666';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#404040';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        <div style={{
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          fontWeight: 500,
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <UserOutlined />
                          Nombre
                        </div>
                        <div style={{
                          color: '#ffffff',
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 500,
                        }}>
                          {alumno.alu_nombre}
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={12}>
                      <div style={{
                        background: '#1a1a1a',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #404040',
                        height: '100%',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#666666';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#404040';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        <div style={{
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          fontWeight: 500,
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <UserOutlined />
                          Apellido
                        </div>
                        <div style={{
                          color: '#ffffff',
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 500,
                        }}>
                          {alumno.alu_apellido}
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={12}>
                      <div style={{
                        background: '#1a1a1a',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #404040',
                        height: '100%',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#666666';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#404040';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        <div style={{
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          fontWeight: 500,
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <MailOutlined />
                          Email
                        </div>
                        <div style={{
                          color: '#ffffff',
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 500,
                          wordBreak: 'break-word',
                        }}>
                          {alumno.alu_email}
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={12}>
                      <div style={{
                        background: '#1a1a1a',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #404040',
                        height: '100%',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#666666';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#404040';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        <div style={{
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          fontWeight: 500,
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <PhoneOutlined />
                          Teléfono
                        </div>
                        <div style={{
                          color: '#ffffff',
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 500,
                        }}>
                          {alumno.alu_telefono || <span style={{ color: '#888888', fontStyle: 'italic' }}>No registrado</span>}
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={12}>
                      <div style={{
                        background: '#1a1a1a',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #404040',
                        height: '100%',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#666666';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#404040';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        <div style={{
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          fontWeight: 500,
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <CalendarOutlined />
                          Fecha de Nacimiento
                        </div>
                        <div style={{
                          color: '#ffffff',
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 500,
                        }}>
                          {alumno.alu_fecha_nacimiento
                            ? dayjs(alumno.alu_fecha_nacimiento).format('DD/MM/YYYY')
                            : <span style={{ color: '#888888', fontStyle: 'italic' }}>No registrada</span>}
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={12}>
                      <div style={{
                        background: '#1a1a1a',
                        padding: isMobile ? '16px' : '20px',
                        borderRadius: '12px',
                        border: '1px solid #404040',
                        height: '100%',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#666666';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#404040';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        <div style={{
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          fontWeight: 500,
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <ManOutlined />
                          Sexo
                        </div>
                        <div style={{
                          color: '#ffffff',
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 500,
                        }}>
                          {alumno.alu_sexo || <span style={{ color: '#888888', fontStyle: 'italic' }}>No registrado</span>}
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} sm={12}>
                      <div style={{
                        background: '#1a1a1a',
                        padding: isMobile ? '16px' : '20px',
                        borderRadius: '12px',
                        border: '1px solid #404040',
                        height: '100%',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#666666';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#404040';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        <div style={{
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          fontWeight: 500,
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <TagOutlined />
                          Categoría
                        </div>
                        <div style={{
                          color: '#ffffff',
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 500,
                        }}>
                          {alumno.cat_descripcion || <span style={{ color: '#888888', fontStyle: 'italic' }}>Sin categoría</span>}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            )}
          </Spin>
        </div>
      </div>
    </MainLayout>
  );
};

export default AlumnoPerfil;

